<!--
  ReservationModal.vue
  예약 추가/수정 모달 컴포넌트
  
  주요 기능:
  - 예약 정보 입력/수정
  - 폼 유효성 검사
  - 예약 상태 설정
-->
<template>
  <a-modal
    :visible="open"
    :confirm-loading="loading"
    :footer="null"
    @cancel="handleCancel"
    width="600px"
  >
    <template #title>
      <div class="modal-title">
        예약 상세
      </div>
    </template>
    
    <div class="modal-content">
      <a-form
        ref="formRef"
        :model="formData"
        layout="vertical"
      >
        <!-- 고객 정보 -->
        <div class="form-section">
          <h4 class="section-title">고객 정보</h4>
          
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="고객명" name="customerName">
                <a-input 
                  v-model:value="formData.customerName"
                  placeholder="고객명을 입력하세요"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="전화번호" name="phoneNumber">
                <a-input 
                  v-model:value="formData.phoneNumber"
                  placeholder="전화번호를 입력하세요"
                />
              </a-form-item>
            </a-col>
          </a-row>
          
          <a-form-item label="생년월일" name="birthDate">
            <a-date-picker
              v-model:value="formData.birthDate"
              format="YYYY-MM-DD"
              placeholder="생년월일을 선택하세요"
              style="width: 100%"
            />
          </a-form-item>
        </div>

        <!-- 예약 정보 -->
        <div class="form-section">
          <h4 class="section-title">예약 정보</h4>
          
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="병원명" name="companyName">
                <a-input 
                  v-model:value="formData.companyName"
                  placeholder="병원명을 입력하세요"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="시술명" name="categoryName">
                <a-input 
                  v-model:value="formData.categoryName"
                  placeholder="시술명을 입력하세요"
                />
              </a-form-item>
            </a-col>
          </a-row>
          
          <a-row :gutter="16">
            <a-col :span="12">
              <a-form-item label="예약 희망 시간" name="preferredTime">
                <a-input 
                  v-model:value="formData.preferredTime"
                  placeholder="예: 오전 10시, 오후 2시"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item label="예약 상태" name="reservationStatus">
                <a-select 
                  v-model:value="formData.reservationStatus"
                  placeholder="예약 상태를 선택하세요"
                >
                  <a-select-option value="PENDING">예약 대기</a-select-option>
                  <a-select-option value="APPROVED">예약 확정</a-select-option>
                  <a-select-option value="COMPLETED">진료 완료</a-select-option>
                  <a-select-option value="CANCELLED">예약 취소</a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          
          <a-form-item label="특이사항" name="notes">
            <a-textarea 
              v-model:value="formData.notes"
              placeholder="특이사항이 있으면 입력하세요"
              :rows="3"
            />
          </a-form-item>
        </div>
      </a-form>
      
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <!-- <a-button 
          type="primary" 
          :loading="loading"
          @click="handleSubmit"
          class="yellow-button"
        >
          {{ isEdit ? '수정' : '추가' }}
        </a-button> -->
      </div>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, watch } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';

export default {
  name: 'ReservationModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    reservationData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    const formRef = ref();
    
    // 폼 데이터
    const formData = reactive({
      customerName: '',
      phoneNumber: '',
      birthDate: null,
      companyName: '',
      categoryName: '',
      preferredTime: '',
      reservationStatus: 'PENDING',
      notes: ''
    });

    // 폼 초기화
    const resetForm = () => {
      Object.assign(formData, {
        customerName: '',
        phoneNumber: '',
        birthDate: null,
        companyName: '',
        categoryName: '',
        preferredTime: '',
        reservationStatus: 'PENDING',
        notes: ''
      });
    };

    // 폼 유효성 검사
    const validateForm = () => {
      if (!formData.customerName.trim()) {
        message.warning('고객명을 입력해주세요.');
        return false;
      }
      
      if (!formData.phoneNumber.trim()) {
        message.warning('전화번호를 입력해주세요.');
        return false;
      }
      
      // 전화번호 형식 검사 (간단한 검사)
      const phoneRegex = /^[0-9-]+$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        message.warning('올바른 전화번호 형식을 입력해주세요.');
        return false;
      }
      
      if (!formData.companyName.trim()) {
        message.warning('병원명을 입력해주세요.');
        return false;
      }
      
      if (!formData.categoryName.trim()) {
        message.warning('시술명을 입력해주세요.');
        return false;
      }
      
      if (!formData.reservationStatus) {
        message.warning('예약 상태를 선택해주세요.');
        return false;
      }
      
      return true;
    };

    // 제출 처리
    const handleSubmit = () => {
      if (!validateForm()) {
        return;
      }
      
      // 데이터 정리
      const submitData = {
        customerName: formData.customerName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        birthDate: formData.birthDate ? dayjs(formData.birthDate).format('YYYY-MM-DD') : null,
        companyName: formData.companyName.trim(),
        categoryName: formData.categoryName.trim(),
        preferredTime: formData.preferredTime.trim(),
        reservationStatus: formData.reservationStatus,
        notes: formData.notes.trim()
      };
      
      // 빈 값 제거
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === null) {
          delete submitData[key];
        }
      });
      
      emit('ok', submitData);
    };

    // 취소 처리
    const handleCancel = () => {
      emit('cancel');
    };

    // props 변경 감지하여 폼 데이터 설정
    watch(() => props.reservationData, (newData) => {
      if (newData && Object.keys(newData).length > 0) {
        Object.assign(formData, {
          customerName: newData.customerName || '',
          phoneNumber: newData.phoneNumber || '',
          birthDate: newData.birthDate ? dayjs(newData.birthDate) : null,
          companyName: newData.companyName || '',
          categoryName: newData.categoryName || '',
          preferredTime: newData.preferredTime || '',
          reservationStatus: newData.reservationStatus || 'PENDING',
          notes: newData.notes || ''
        });
      }
    }, { immediate: true, deep: true });

    // 모달이 닫힐 때 폼 초기화
    watch(() => props.open, (newOpen) => {
      if (!newOpen) {
        resetForm();
      }
    });

    return {
      formRef,
      formData,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped lang="scss">
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.form-section {
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

:deep(.ant-form-item-label) {
  padding: 0 0 8px;
  
  label {
    color: #000;
    font-size: 14px;
    height: auto;
    font-weight: 500;
  }
}

:deep(.ant-form-item-control-input) {
  .ant-input, .ant-select-selector, .ant-picker {
    height: 48px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    
    &:hover, &:focus {
      border-color: #FFCC32;
      box-shadow: none;
    }
  }
  
  .ant-select-selector {
    padding: 0 12px;
  }
  
  .ant-picker {
    padding: 0 12px;
  }
  
  .ant-input {
    padding: 12px;
  }
  
  .ant-select-selection-item {
    line-height: 46px;
  }
}

:deep(.ant-form-item-control-input) {
  .ant-input.ant-input-textarea {
    height: auto;
    min-height: 80px;
    padding: 12px;
    resize: vertical;
  }
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .modal-content {
    padding: 16px;
  }
  
  .modal-footer {
    flex-direction: column;
    
    .ant-btn {
      width: 100%;
    }
  }
  
  :deep(.ant-col) {
    margin-bottom: 16px;
  }
}
</style> 